import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api } from "../../api";
import { APageHead, ATable, ABadge } from "../adminKit";

const CATS = ["Account", "Billing", "AI", "Credit report", "Dispute letter", "Vendor tracking", "Document Vault", "Funding readiness", "Technical issue", "Privacy request", "Other"];
const STATUSES = ["open", "in_progress", "waiting_for_user", "resolved", "closed"];

export default function AdminSupport() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", category: "Account", priority: "normal", user_email: "" });

  const load = () => api.get("/admin/support", { params: { status: filter } }).then(({ data }) => setRows(data));
  useEffect(() => { load(); }, [filter]);

  const create = async () => {
    if (!form.subject.trim()) return toast.error("Subject required");
    try { await api.post("/admin/support", form); setOpen(false); setForm({ subject: "", category: "Account", priority: "normal", user_email: "" }); toast.success("Ticket created"); load(); }
    catch { toast.error("Failed"); }
  };
  const update = async (id, patch) => { try { await api.patch(`/admin/support/${id}`, patch); load(); } catch { toast.error("Failed"); } };

  return (
    <div className="space-y-4">
      <APageHead title="Support Management" subtitle="Manage support cases with internal notes separated from user responses."
        action={<div className="flex gap-2">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="cc-input rounded-lg px-3 py-2 text-sm">
            <option value="" className="bg-[#1a1a24]">All statuses</option>
            {STATUSES.map((s) => <option key={s} className="bg-[#1a1a24]">{s}</option>)}
          </select>
          <button onClick={() => setOpen(true)} data-testid="ticket-new" className="cc-btn-primary inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-500"><Plus size={15} /> New Ticket</button>
        </div>} />

      <ATable loading={false} rows={rows} columns={[
        { key: "subject", label: "Subject", render: (r) => <span className="text-white">{r.subject}</span> },
        { key: "category", label: "Category", render: (r) => <ABadge tone="blue">{r.category}</ABadge> },
        { key: "user_email", label: "User" },
        { key: "priority", label: "Priority", render: (r) => <ABadge tone={r.priority === "high" ? "red" : "default"}>{r.priority}</ABadge> },
        { key: "status", label: "Status", render: (r) => (
          <select value={r.status} onChange={(e) => update(r.id, { status: e.target.value })} onClick={(e) => e.stopPropagation()} className="cc-input rounded-lg px-2 py-1 text-xs">
            {STATUSES.map((s) => <option key={s} className="bg-[#1a1a24]">{s}</option>)}
          </select>
        )},
        { key: "created_at", label: "Created", render: (r) => new Date(r.created_at).toLocaleDateString() },
      ]} empty="No support tickets." />

      {open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <div className="relative cc-card rounded-2xl p-6 w-full max-w-md space-y-3">
            <h3 className="cc-heading text-lg font-500 text-white">New Support Ticket</h3>
            <input placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="cc-input w-full rounded-xl px-4 py-2.5 text-sm" data-testid="ticket-subject" />
            <input placeholder="User email" value={form.user_email} onChange={(e) => setForm({ ...form, user_email: e.target.value })} className="cc-input w-full rounded-xl px-4 py-2.5 text-sm" />
            <div className="grid grid-cols-2 gap-3">
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="cc-input rounded-xl px-3 py-2.5 text-sm">{CATS.map((c) => <option key={c} className="bg-[#1a1a24]">{c}</option>)}</select>
              <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="cc-input rounded-xl px-3 py-2.5 text-sm">{["low", "normal", "high", "urgent"].map((p) => <option key={p} className="bg-[#1a1a24]">{p}</option>)}</select>
            </div>
            <button onClick={create} className="cc-btn-primary w-full rounded-xl py-2.5 text-sm font-500" data-testid="ticket-create">Create Ticket</button>
          </div>
        </div>
      )}
    </div>
  );
}
